import Image from "next/image";
import { useMedia } from "react-use";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/v2-components/ui/dialog";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/v2-components/ui/sheet";
import { Button } from "@/v2-components/ui/button";
import CrossIcon from "@/v2-components/core/icon/cross-icon";
import { useTrans } from "@/utils/useTrans";
import { cn } from "@/libs/utils";

/**
 * 담기 계열 모달의 **공용 껍데기** — 데스크톱 다이얼로그 / 모바일 바텀 드로어.
 *
 * ## 왜 지금 뽑았나
 *
 * 같은 셸을 쓰는 모달이 **셋째**가 됐다(#5355 담기 확인 · #5356 번들 병합). 두 개일 때는
 * 각자 두고 **소스 가드로 교차 검사**했는데(`added-to-bag-trigger.test.ts` 의
 * 「모달 표면 규약」), 셋째가 생기면 그 목록을 늘리는 것보다 셸을 하나로 두는 쪽이 맞다 —
 * #5356 착수 시점에 판단하기로 이슈에 적어뒀고 그 시점이 여기다.
 *
 * 셸이 **실제로 동일**함을 확인하고 뽑았다(시안 대조 + 코드 대조):
 *   Sheet   `flex h-auto flex-col gap-5 rounded-t-2xl px-4 pb-6 pt-3` + 그래버 + 헤더
 *   Dialog  `z-[100] flex max-w-[400px] flex-col gap-6 p-6` + 헤더
 *   제목    두 표면 동일 16/22 medium `tracking-[0.15px]`
 *
 * ⚠️ **결제 선택 모달(#5293)은 포함하지 않는다.** 그쪽은 수치가 다르다
 * (`max-w-[480px] gap-7 rounded-lg p-8` · 제목이 표면마다 22/28·24/32로 갈림).
 * 억지로 합치면 className 프롭이 네 개로 늘어 추상이 새어나간다.
 *
 * ## 여기에 모아둔 「기본값 함정」 — 개별 모달에서 다시 틀리지 않게
 *
 * · `SheetHeader` 기본값 **셋**을 덮는다: `-mt-4`(음수 마진 → 그래버와 겹침) · `py-5` ·
 *   `text-center sm:text-left`(모바일에서 제목만 가운데). **하나만 덮으면 나머지가 남는다.**
 * · `SheetContent` 에 **`flex flex-col`** 을 준다 — `sheetVariants` 에 display 가 없어 기본이
 *   `block` 이고 그러면 `gap-5` 가 조용히 무효가 된다(#5293 실기 결함).
 * · `SheetClose` 는 `DialogClose` 와 달리 **아이콘을 안 그린다** → 직접 그린다.
 * · 브레이크포인트는 형제 `retailer-sheet.tsx` 와 **같은 1023px** — 갈리면 같은 폭에서
 *   한쪽만 시트가 된다.
 */
export function BagModalShell({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  const { t } = useTrans(["gnb"]);
  const isMobile = useMedia("(max-width: 1023px)", false);

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
        <SheetContent
          side="bottom"
          aria-describedby={undefined}
          className="flex h-auto flex-col gap-5 rounded-t-2xl px-4 pb-6 pt-3"
        >
          {/* 그래버 40×4 · `Cool Gray/10` · r2 */}
          <div className="flex justify-center">
            <span className="h-1 w-10 rounded-sm bg-coolgray-10" />
          </div>
          <SheetHeader className="mt-0 items-center justify-between gap-2 py-0 text-left laptop:mt-0 laptop:py-0">
            <SheetTitle className="min-w-0 flex-1 text-lg font-500 tracking-[0.15px] text-coolgray-90">
              {title}
            </SheetTitle>
            <SheetClose
              aria-label={t("Close")}
              className="shrink-0 focus:outline-none"
            >
              <CrossIcon />
            </SheetClose>
          </SheetHeader>
          {children}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="z-[100] flex max-w-[400px] flex-col gap-6 p-6">
        <DialogHeader className="mb-0 items-center gap-3">
          <DialogTitle className="min-w-0 flex-1 text-lg font-500 tracking-[0.15px] text-coolgray-90">
            {title}
          </DialogTitle>
          <DialogClose />
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}

/**
 * 제품 카드 — 테두리 `Cool Gray/20` · r8 · `p-16` · `gap-16`.
 *
 * 가격 영역은 모달마다 다르므로(`children`) 껍데기만 공유한다 —
 * 담기 확인은 판매가(+할인 시 줄긋기), 번들 병합은 거기에 **절약액 줄**이 더 붙는다.
 */
export function BagModalProduct({
  thumbnail,
  name,
  options,
  thumbnailClassName,
  nameClassName,
  children,
}: {
  thumbnail: string;
  name: string;
  /** `[{type,value}]` — 라벨을 붙여 `Color: Onyx · Size: L` 로 그린다. */
  options: { type: string; value: string }[];
  /** 시안이 모달마다 다르다 — 담기 확인은 데스크톱 80px, 번들 병합은 64px. */
  thumbnailClassName?: string;
  /** 담기 확인은 medium/`tracking-[0.1px]`, 번들 병합은 semibold/`tracking-[0.25px]`. */
  nameClassName?: string;
  children?: React.ReactNode;
}) {
  const { t } = useTrans(["gnb"]);

  return (
    <div className="flex items-center gap-4 rounded-lg border border-coolgray-20 p-4">
      <div className={cn("relative size-16 shrink-0", thumbnailClassName)}>
        {/* ⚠️ 장식용이라 `alt=""` — 제품명이 바로 옆에 있어 `alt` 에 이름을 넣으면
            스크린리더가 두 번 읽고 **영역을 복사해도 이름이 두 번** 붙는다(실기 제보). */}
        <Image
          src={thumbnail}
          alt=""
          fill
          className="object-cover"
          sizes="80px"
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className={cn("text-base text-coolgray-90", nameClassName)}>
          {name}
        </p>
        {options.length > 0 && (
          <p className="text-sm tracking-[0.4px] text-coolgray-60">
            {/* 시안은 **라벨을 붙인다** — 값만 찍으면 "ash" 처럼 무슨 옵션인지 알 수 없다.
                타입 집합은 정확히 둘이다(`ProductUtil.getOptionsFromSkuOption` 이 위치로
                생성: `idx === 0 ? "color" : "size"`). 값은 데이터가 소문자라 `capitalize`. */}
            {options.map((o, i) => (
              <span key={`${o.type}-${o.value}`}>
                {i > 0 && " · "}
                {t(OPTION_LABEL[o.type] ?? o.type)}:{" "}
                <span className="capitalize">{o.value}</span>
              </span>
            ))}
          </p>
        )}
        {children}
      </div>
    </div>
  );
}

/**
 * 옵션 타입 → 번역 키. **집합은 정확히 둘**이다 — `ProductUtil.getOptionsFromSkuOption` 이
 * 위치로 만든다(`idx === 0 ? "color" : "size"`). `Color`·`Size` 키는 `gnb` 에 이미 있다.
 */
const OPTION_LABEL: Record<string, string> = { color: "Color", size: "Size" };

/**
 * 하단 버튼 두 개 — 데스크톱 나란히(각 flex-1), 모바일 세로 스택. 둘 다 `gap-8` · h-40 pill.
 *
 * ⚠️ `size="sm"` 을 **명시**한다. 생략하면 기본 `md` 라 `min-h-[54px] text-lg` 가 붙고
 * `h-10` 이 `min-h` 에 덮여 **죽은 클래스**가 된다(#5354 실측).
 */
export function BagModalButtons({
  onKeepShopping,
  onViewCart,
}: {
  onKeepShopping: () => void;
  onViewCart: () => void;
}) {
  const { t } = useTrans(["gnb"]);

  return (
    <div className="flex flex-col gap-2 laptop:flex-row">
      <Button
        size="sm"
        variant="blue-outline"
        shape="pill"
        className="h-10 w-full px-6 laptop:min-w-0 laptop:flex-1"
        onClick={onKeepShopping}
      >
        {t("Keep Shopping")}
      </Button>
      <Button
        size="sm"
        variant="blue-fill"
        shape="pill"
        className="h-10 w-full px-6 laptop:min-w-0 laptop:flex-1"
        onClick={onViewCart}
      >
        {t("View Bag")}
      </Button>
    </div>
  );
}
